<template>
  <div class="sales-dashboard">
    <!-- 6 Period Cards - 3 per row -->
    <div class="period-grid">
      <div 
        v-for="tab in periodTabs" 
        :key="tab.key"
        class="period-card"
        :class="{ 'active': activePeriod === tab.key }"
        @click="openPeriodModal(tab.key)"
      >
        <div class="period-card-inner">
          <div class="period-card-header">
            <span class="period-label">{{ tab.label }}</span>
            <span v-if="tab.change !== null && !isNaN(tab.change)" 
                  class="period-change" 
                  :class="tab.change >= 0 ? 'positive' : 'negative'">
              <b-icon :icon="tab.change >= 0 ? 'arrow-up' : 'arrow-down'" size="sm" />
              {{ Math.abs(tab.change).toFixed(1) }}%
            </span>
          </div>
          <div class="period-amount">{{ formatCurrency(tab.value) }}</div>
          <div class="period-transactions">{{ tab.count }} transactions</div>
          <div class="period-card-indicator" :class="{ 'active': activePeriod === tab.key }"></div>
          
          <!-- Mini Donut Chart for each period -->
          <div class="period-donut-container">
            <canvas :id="'donut_' + tab.key" class="period-donut"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Period Detail Modal with Carousel -->
    <b-modal 
      v-model="showPeriodModal" 
      :title="modalTitle"
      size="xl"
      hide-footer
      class="period-modal"
      @hidden="onModalClose"
    >
      <div v-if="isModalLoading" class="loading-container">
        <div class="loader-3d">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loading-text">Loading period data...</p>
      </div>
      
      <div v-else>
        <!-- Modal Summary -->
        <div class="modal-summary">
          <div class="modal-summary-item" v-for="(item, index) in summaryItems" :key="index">
            <div class="summary-icon-wrapper">
              <span class="summary-icon">{{ item.icon }}</span>
            </div>
            <div>
              <span class="label">{{ item.label }}</span>
              <span class="value">{{ item.value }}</span>
            </div>
          </div>
        </div>

        <!-- Carousel Navigation -->
        <div class="carousel-nav">
          <button 
            class="carousel-btn prev" 
            @click="prevSlide"
            :disabled="currentSlide === 0"
          >
            <b-icon-chevron-left size="lg" />
          </button>
          <div class="carousel-dots">
            <span 
              v-for="(slide, index) in carouselSlides" 
              :key="index"
              class="dot"
              :class="{ 'active': currentSlide === index }"
              @click="currentSlide = index"
            ></span>
          </div>
          <button 
            class="carousel-btn next" 
            @click="nextSlide"
            :disabled="currentSlide === carouselSlides.length - 1"
          >
            <b-icon-chevron-right size="lg" />
          </button>
        </div>

        <!-- Carousel Slides -->
        <div class="carousel-container">
          <div 
            class="carousel-track"
            :style="{ transform: 'translateX(-' + (currentSlide * 100) + '%)' }"
          >
            <!-- Slide 1: Sales by Location -->
            <div class="carousel-slide">
              <div class="slide-content">
                <div class="slide-header">
                  <div class="slide-title">
                    <span class="slide-icon">📍</span>
                    <h6>Sales Performance by Location</h6>
                  </div>
                  <span class="slide-count">{{ modalLocationStats.length }} locations</span>
                </div>
                <div class="table-wrap">
                  <table class="modal-table clickable-rows">
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th class="text-right">Sales</th>
                        <th class="text-right">Net</th>
                        <th class="text-right">Orders</th>
                        <th class="text-right">Avg Order</th>
                        <th class="text-right">% of Total</th>
                        <th class="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in modalLocationStats" :key="item.location_id" @click="drillDownLocation(item)">
                        <td><strong>{{ item.location_name }}</strong></td>
                        <td class="text-right">{{ formatCurrency(item.sales) }}</td>
                        <td class="text-right">{{ formatCurrency(item.net) }}</td>
                        <td class="text-right">{{ item.orders }}</td>
                        <td class="text-right">{{ formatCurrency(item.avg_order) }}</td>
                        <td class="text-right">
                          <span class="percentage-badge">{{ item.percentage }}%</span>
                        </td>
                        <td class="text-center">
                          <b-button size="sm" variant="outline-primary" @click.stop="drillDownLocation(item)">
                            <b-icon-search size="sm" />
                          </b-button>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(modalTotalLocationSales) }}</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(modalTotalLocationNet) }}</strong></td>
                        <td class="text-right"><strong>{{ modalTotalLocationOrders }}</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(modalTotalLocationAvg) }}</strong></td>
                        <td class="text-right"><strong>100%</strong></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div class="table-hint">
                  <b-icon-info-circle size="sm" class="mr-1" />
                  Click on any location to view detailed breakdown
                </div>
              </div>
            </div>

            <!-- Slide 2: Sales by Salesperson -->
            <div class="carousel-slide">
              <div class="slide-content">
                <div class="slide-header">
                  <div class="slide-title">
                    <span class="slide-icon">👤</span>
                    <h6>Sales Performance by Salesperson</h6>
                  </div>
                  <span class="slide-count">{{ modalSalespersonStats.length }} salespeople</span>
                </div>
                <div class="table-wrap">
                  <table class="modal-table clickable-rows">
                    <thead>
                      <tr>
                        <th>Salesperson</th>
                        <th class="text-right">Sales</th>
                        <th class="text-right">Net</th>
                        <th class="text-right">Orders</th>
                        <th class="text-right">Avg Order</th>
                        <th class="text-right">% of Total</th>
                        <th class="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in modalSalespersonStats" :key="item.salesperson" @click="drillDownSalesperson(item)">
                        <td><strong>{{ item.salesperson }}</strong></td>
                        <td class="text-right">{{ formatCurrency(item.sales) }}</td>
                        <td class="text-right">{{ formatCurrency(item.net) }}</td>
                        <td class="text-right">{{ item.orders }}</td>
                        <td class="text-right">{{ formatCurrency(item.avg_order) }}</td>
                        <td class="text-right">
                          <span class="percentage-badge">{{ item.percentage }}%</span>
                        </td>
                        <td class="text-center">
                          <b-button size="sm" variant="outline-primary" @click.stop="drillDownSalesperson(item)">
                            <b-icon-search size="sm" />
                          </b-button>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(modalTotalSalespersonSales) }}</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(modalTotalSalespersonNet) }}</strong></td>
                        <td class="text-right"><strong>{{ modalTotalSalespersonOrders }}</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(modalTotalSalespersonAvg) }}</strong></td>
                        <td class="text-right"><strong>100%</strong></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div class="table-hint">
                  <b-icon-info-circle size="sm" class="mr-1" />
                  Click on any salesperson to view detailed breakdown
                </div>
              </div>
            </div>

            <!-- Slide 3: Recent Transactions -->
            <div class="carousel-slide">
              <div class="slide-content">
                <div class="slide-header">
                  <div class="slide-title">
                    <span class="slide-icon">📋</span>
                    <h6>Recent Transactions</h6>
                  </div>
                  <span class="slide-count">{{ modalTransactions.length }} transactions</span>
                </div>
                <div class="table-wrap" style="max-height: 400px;">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Invoice</th>
                        <th>Location</th>
                        <th>Salesperson</th>
                        <th>Payment</th>
                        <th class="text-right">Amount</th>
                        <th class="text-center">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in modalPaginatedTransactions" :key="item.invoice_id + item.ts">
                        <td>{{ formatDate(item.ts) }}</td>
                        <td>{{ item.invoice_id }}</td>
                        <td>{{ getLocationName(item.location_id) }}</td>
                        <td>{{ item.username }}</td>
                        <td>{{ item.payment_methods ? item.payment_methods.join(', ') : '-' }}</td>
                        <td class="text-right" :class="{ 'text-danger': item.total_dollar_amount < 0 }">
                          {{ formatCurrency(item.total_dollar_amount) }}
                        </td>
                        <td class="text-center">
                          <b-badge :variant="item.is_refund_entry ? 'danger' : 'success'" pill>
                            {{ item.is_refund_entry ? 'Refund' : 'Sale' }}
                          </b-badge>
                        </td>
                      </tr>
                      <tr v-if="!modalTransactions.length">
                        <td colspan="7" class="text-center py-3 text-muted">No transactions found</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="modalTransactions.length" class="modal-pagination">
                  <small class="text-muted">
                    {{ (modalCurrentPage - 1) * modalPageSize + 1 }} - {{ Math.min(modalCurrentPage * modalPageSize, modalTransactions.length) }} of {{ modalTransactions.length }}
                  </small>
                  <b-pagination
                    v-model="modalCurrentPage"
                    :total-rows="modalTransactions.length"
                    :per-page="modalPageSize"
                    size="sm"
                    class="mb-0"
                  />
                  <b-form-select v-model="modalPageSize" size="sm" style="width: 70px;">
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                  </b-form-select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Slide Counter -->
        <div class="slide-counter">
          {{ currentSlide + 1 }} / {{ carouselSlides.length }}
        </div>
      </div>
    </b-modal>

    <!-- Drill Down Modal -->
    <b-modal 
      v-model="showDrillDownModal" 
      :title="drillDownTitle"
      size="lg"
      hide-footer
      class="drilldown-modal"
      @hidden="onDrillDownClose"
    >
      <div v-if="isDrillDownLoading" class="loading-container">
        <div class="loader-3d">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loading-text">Loading details...</p>
      </div>
      
      <div v-else>
        <!-- Drill Down Summary -->
        <div class="modal-summary">
          <div class="modal-summary-item" v-for="(item, index) in drillDownSummaryItems" :key="index">
            <div class="summary-icon-wrapper">
              <span class="summary-icon">{{ item.icon }}</span>
            </div>
            <div>
              <span class="label">{{ item.label }}</span>
              <span class="value">{{ item.value }}</span>
            </div>
          </div>
        </div>

        <!-- Drill Down Content -->
        <div class="drilldown-content">
          <!-- Payment Methods Pie Chart -->
          <div class="drilldown-section">
            <h6 class="drilldown-section-title">💳 Payment Methods</h6>
            <div class="drilldown-payment-grid">
              <div class="drilldown-chart-container">
                <canvas ref="drillDownPieChart" id="drillDownPieChart"></canvas>
              </div>
              <div class="drilldown-payment-list">
                <div 
                  v-for="(item, index) in drillDownPaymentStats" 
                  :key="index"
                  class="payment-item-modal"
                >
                  <div class="payment-row-modal">
                    <span class="payment-name-modal">
                      <span class="payment-dot" :style="{ background: colors[index % colors.length] }"></span>
                      {{ item.method }}
                    </span>
                    <span class="payment-amount-modal">{{ formatCurrency(item.amount) }}</span>
                  </div>
                  <div class="payment-bar-modal">
                    <div 
                      class="payment-bar-fill-modal" 
                      :style="{ width: item.percentage + '%', background: colors[index % colors.length] }"
                    />
                  </div>
                  <div class="payment-row-modal small text-muted">
                    <span>{{ item.count }} transactions</span>
                    <span>{{ item.percentage }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Stats based on drill down type -->
          <div class="drilldown-section" v-if="drillDownType === 'location'">
            <h6 class="drilldown-section-title">👤 Top Salespeople at this Location</h6>
            <div class="table-wrap">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th class="text-right">Sales</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">Avg Order</th>
                    <th class="text-right">% of Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in drillDownSalespersonStats" :key="item.salesperson">
                    <td><strong>{{ item.salesperson }}</strong></td>
                    <td class="text-right">{{ formatCurrency(item.sales) }}</td>
                    <td class="text-right">{{ item.orders }}</td>
                    <td class="text-right">{{ formatCurrency(item.avg_order) }}</td>
                    <td class="text-right">
                      <span class="percentage-badge">{{ item.percentage }}%</span>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(drillDownTotalSalespersonSales) }}</strong></td>
                    <td class="text-right"><strong>{{ drillDownTotalSalespersonOrders }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(drillDownTotalSalespersonAvg) }}</strong></td>
                    <td class="text-right"><strong>100%</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div class="drilldown-section" v-if="drillDownType === 'salesperson'">
            <h6 class="drilldown-section-title">📍 Locations where this salesperson sold</h6>
            <div class="table-wrap">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="text-right">Sales</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">Avg Order</th>
                    <th class="text-right">% of Salesperson</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in drillDownLocationStats" :key="item.location_id">
                    <td><strong>{{ item.location_name }}</strong></td>
                    <td class="text-right">{{ formatCurrency(item.sales) }}</td>
                    <td class="text-right">{{ item.orders }}</td>
                    <td class="text-right">{{ formatCurrency(item.avg_order) }}</td>
                    <td class="text-right">
                      <span class="percentage-badge">{{ item.percentage }}%</span>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(drillDownTotalLocationSales) }}</strong></td>
                    <td class="text-right"><strong>{{ drillDownTotalLocationOrders }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(drillDownTotalLocationAvg) }}</strong></td>
                    <td class="text-right"><strong>100%</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Active Period Banner -->
    <div v-if="activePeriod" class="active-banner">
      <div class="banner-info">
        <span class="banner-label">{{ activePeriodLabel }}</span>
        <span class="banner-amount">{{ formatCurrency(activePeriodValue) }}</span>
        <span class="banner-count">{{ activePeriodCount }} transactions</span>
      </div>
      <b-button variant="link" size="sm" @click="clearPeriodFilter" class="banner-close">
        <b-icon-x />
      </b-button>
    </div>

    <!-- Filters -->
    <div v-if="activePeriod" class="filters-section">
      <b-row>
        <b-col lg="2" md="4" sm="6" class="mb-2">
          <label class="filter-label">Location</label>
          <b-form-select
            v-model="filters.location"
            :options="locationOptions"
            size="sm"
            @change="onFilterChange"
            class="filter-select"
          >
            <template #first>
              <b-form-select-option :value="null">All Locations</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col lg="2" md="4" sm="6" class="mb-2">
          <label class="filter-label">Salesperson</label>
          <b-form-select
            v-model="filters.salesperson"
            :options="salespersonOptions"
            size="sm"
            @change="onFilterChange"
            class="filter-select"
          >
            <template #first>
              <b-form-select-option :value="null">All Salespeople</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col lg="2" md="4" sm="6" class="mb-2">
          <label class="filter-label">Payment Method</label>
          <b-form-select
            v-model="filters.paymentMethod"
            :options="paymentMethodOptions"
            size="sm"
            @change="onFilterChange"
            class="filter-select"
          >
            <template #first>
              <b-form-select-option :value="null">All Methods</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col lg="2" md="4" sm="6" class="mb-2">
          <label class="filter-label">Sale Type</label>
          <b-form-select
            v-model="filters.saleType"
            :options="saleTypeOptions"
            size="sm"
            @change="onFilterChange"
            class="filter-select"
          >
            <template #first>
              <b-form-select-option :value="null">All Types</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col lg="4" class="mb-2">
          <label class="filter-label">Date Range</label>
          <div class="date-range-wrapper">
            <date-picker
              v-model="dateRange"
              format="MM-DD-YYYY"
              value-type="format"
              class="custom-datepicker"
              placeholder="Select Date Range"
              range
              @change="onDateRangeChange"
            />
            <b-button 
              size="sm" 
              variant="primary" 
              @click="fetchMainData"
              :disabled="isLoading"
              class="analyze-btn"
            >
              <b-icon-search v-if="!isLoading" size="sm" />
              <b-icon-arrow-repeat v-else spin size="sm" />
            </b-button>
          </div>
        </b-col>
      </b-row>
      <div v-if="activeFilterCount > 0" class="filter-clear">
        <b-button variant="link" size="sm" @click="clearAllFilters" class="p-0">
          <b-icon-x-circle class="mr-1" />Clear {{ activeFilterCount }} filter(s)
        </b-button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-container">
      <div class="loader-3d">
        <div class="cube">
          <div class="face front"></div>
          <div class="face back"></div>
          <div class="face right"></div>
          <div class="face left"></div>
          <div class="face top"></div>
          <div class="face bottom"></div>
        </div>
      </div>
      <p class="loading-text">Loading sales data...</p>
    </div>

    <!-- Main Content -->
    <transition name="slide-fade" mode="out-in">
      <div v-if="!isLoading && hasData && activePeriod" class="main-content" key="main">
        <!-- Summary Cards - 4 across -->
        <div class="summary-grid">
          <div class="summary-card" v-for="(card, index) in summaryCards" :key="index">
            <div class="summary-icon" :class="card.iconClass">
              <span>{{ card.icon }}</span>
            </div>
            <div class="summary-info">
              <span class="summary-label">{{ card.label }}</span>
              <span class="summary-value">{{ card.value }}</span>
            </div>
          </div>
        </div>

        <!-- Payment Methods - Pie Chart & Breakdown -->
        <div class="payment-section">
          <div class="section-header">
            <b-icon-credit-card class="mr-2" />
            <span>Payment Methods</span>
            <b-badge variant="light" class="ml-2">{{ paymentMethodStats.length }}</b-badge>
          </div>
          <div class="payment-grid">
            <div class="pie-chart-wrapper">
              <canvas ref="pieChart" id="paymentPieChart"></canvas>
            </div>
            <div class="payment-list">
              <div 
                v-for="(item, index) in paymentMethodStats" 
                :key="index"
                class="payment-item"
                @click="drillDownPaymentMethod(item)"
                :class="{ 'drill-active': isPaymentMethodActive(item.method) }"
              >
                <div class="payment-row">
                  <span class="payment-name">
                    <span class="payment-dot" :style="{ background: colors[index % colors.length] }"></span>
                    {{ item.method }}
                    <b-badge v-if="item.subMethods && item.subMethods.length > 0" variant="light" size="sm" class="ml-1">
                      {{ item.subMethods.length }}
                    </b-badge>
                  </span>
                  <span class="payment-amount">{{ formatCurrency(item.amount) }}</span>
                </div>
                <div class="payment-bar">
                  <div 
                    class="payment-bar-fill" 
                    :style="{ width: item.percentage + '%', background: colors[index % colors.length] }"
                  />
                </div>
                <div class="payment-row small text-muted">
                  <span>{{ item.count }} transactions</span>
                  <span>{{ item.percentage }}%</span>
                </div>
                
                <!-- Sub-methods -->
                <div v-if="item.subMethods && item.subMethods.length > 0" class="sub-methods">
                  <div 
                    v-for="(sub, subIndex) in item.subMethods" 
                    :key="subIndex"
                    class="sub-method-item"
                    @click.stop="drillDown('paymentMethod', sub.name)"
                    :class="{ 'drill-active': filters.paymentMethod === sub.name }"
                  >
                    <span class="sub-method-name">{{ sub.name }}</span>
                    <span class="sub-method-amount">{{ formatCurrency(sub.amount) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="!paymentMethodStats.length" class="text-center py-3 text-muted">
                No payment methods
              </div>
            </div>
          </div>
        </div>

        <!-- Location & Salesperson Performance -->
        <div v-if="hasActiveFilters" class="performance-grid">
          <div class="performance-card">
            <div class="performance-header">
              <b-icon-building class="mr-2" />
              <span>Location Performance</span>
              <b-badge variant="light" class="ml-2">{{ filteredLocationStats.length }}</b-badge>
            </div>
            <div class="performance-table-wrap">
              <table class="performance-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="text-right">Sales</th>
                    <th class="text-right">Net</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="item in filteredLocationStats" 
                    :key="item.location_id"
                    @click="drillDown('location', item.location_id)"
                    :class="{ 'drill-active': filters.location === item.location_id }"
                  >
                    <td>{{ item.location_name }}</td>
                    <td class="text-right">{{ formatCurrency(item.sales) }}</td>
                    <td class="text-right">{{ formatCurrency(item.net) }}</td>
                    <td class="text-right">{{ item.orders }}</td>
                    <td class="text-right">{{ item.percentage }}%</td>
                  </tr>
                </tbody>
                <tfoot v-if="filteredLocationStats.length">
                  <tr>
                    <td><strong>Total</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(filteredTotalLocationSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(filteredTotalLocationNet) }}</strong></td>
                    <td class="text-right"><strong>{{ filteredTotalLocationOrders }}</strong></td>
                    <td class="text-right"><strong>100%</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div class="performance-card">
            <div class="performance-header">
              <b-icon-people class="mr-2" />
              <span>Salesperson Performance</span>
              <b-badge variant="light" class="ml-2">{{ filteredSalespersonStats.length }}</b-badge>
            </div>
            <div class="performance-table-wrap">
              <table class="performance-table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th class="text-right">Sales</th>
                    <th class="text-right">Net</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">Avg Order</th>
                    <th class="text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="item in filteredSalespersonStats" 
                    :key="item.salesperson"
                    @click="drillDown('salesperson', item.salesperson)"
                    :class="{ 'drill-active': filters.salesperson === item.salesperson }"
                  >
                    <td>{{ item.salesperson }}</td>
                    <td class="text-right">{{ formatCurrency(item.sales) }}</td>
                    <td class="text-right">{{ formatCurrency(item.net) }}</td>
                    <td class="text-right">{{ item.orders }}</td>
                    <td class="text-right">{{ formatCurrency(item.avg_order) }}</td>
                    <td class="text-right">{{ item.percentage }}%</td>
                  </tr>
                </tbody>
                <tfoot v-if="filteredSalespersonStats.length">
                  <tr>
                    <td><strong>Total</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(filteredTotalSalespersonSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(filteredTotalSalespersonNet) }}</strong></td>
                    <td class="text-right"><strong>{{ filteredTotalSalespersonOrders }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(filteredTotalSalespersonAvgOrder) }}</strong></td>
                    <td class="text-right"><strong>100%</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <!-- No filters message -->
        <div v-else class="no-filters-message">
          <b-icon-filter-circle class="icon" />
          <p>Select a Location, Salesperson, or Payment Method to view detailed performance</p>
        </div>

        <!-- Detailed Transactions -->
        <div v-if="showDetailed && hasActiveFilters" class="detailed-section">
          <div class="detailed-header">
            <div>
              <b-icon-table class="mr-2" />
              <span>Detailed Transactions</span>
              <b-badge variant="secondary" class="ml-2">{{ filteredTransactions.length }}</b-badge>
            </div>
            <div>
              <b-button size="sm" variant="outline-secondary" @click="exportCSV" class="mr-1">
                <b-icon-download size="sm" class="mr-1" />Export
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="showDetailed = false">
                <b-icon-x size="sm" />Close
              </b-button>
            </div>
          </div>
          <div class="table-wrap" style="max-height: 400px;">
            <table class="performance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Location</th>
                  <th>Salesperson</th>
                  <th>Payment</th>
                  <th class="text-right">Amount</th>
                  <th class="text-center">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in paginatedTransactions" :key="item.invoice_id + item.ts">
                  <td>{{ formatDate(item.ts) }}</td>
                  <td>{{ item.invoice_id }}</td>
                  <td>{{ getLocationName(item.location_id) }}</td>
                  <td>{{ item.username }}</td>
                  <td>{{ item.payment_methods ? item.payment_methods.join(', ') : '-' }}</td>
                  <td class="text-right" :class="{ 'text-danger': item.total_dollar_amount < 0 }">
                    {{ formatCurrency(item.total_dollar_amount) }}
                  </td>
                  <td class="text-center">
                    <b-badge :variant="item.is_refund_entry ? 'danger' : 'success'" pill>
                      {{ item.is_refund_entry ? 'Refund' : 'Sale' }}
                    </b-badge>
                  </td>
                </tr>
                <tr v-if="!filteredTransactions.length">
                  <td colspan="7" class="text-center py-3 text-muted">No transactions found</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="filteredTransactions.length" class="table-pagination">
            <small class="text-muted">
              {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, filteredTransactions.length) }} of {{ filteredTransactions.length }}
            </small>
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredTransactions.length"
              :per-page="pageSize"
              size="sm"
              class="mb-0"
            />
            <b-form-select v-model="pageSize" size="sm" style="width: 70px;">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </b-form-select>
          </div>
        </div>
      </div>
    </transition>

    <!-- Empty States -->
    <div v-if="!isLoading && !hasData && !activePeriod" class="empty-state">
      <div class="empty-icon-wrapper">
        <b-icon-search class="empty-icon" />
      </div>
      <h6 class="mt-3">Select a Period</h6>
      <p class="text-muted small">Click on a period card above to view sales data</p>
    </div>
    
    <div v-if="!isLoading && !hasData && activePeriod" class="empty-state">
      <div class="empty-icon-wrapper">
        <b-icon-search class="empty-icon" />
      </div>
      <h6 class="mt-3">No Data Available</h6>
      <p class="text-muted small">No transactions found for the selected period</p>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import Chart from 'chart.js';
import axios from 'axios';

export default {
  name: 'SalesDashboard',
  components: { DatePicker },
  
  data() {
    return {
      isLoading: false,
      showDetailed: false,
      activePeriod: null,
      
      // Modal
      showPeriodModal: false,
      isModalLoading: false,
      currentSlide: 0,
      modalPeriod: null,
      modalTransactions: [],
      modalPaymentStats: [],
      modalLocationStats: [],
      modalSalespersonStats: [],
      modalCurrentPage: 1,
      modalPageSize: 10,
      modalPieChart: null,
      
      // Drill Down Modal
      showDrillDownModal: false,
      isDrillDownLoading: false,
      drillDownType: null,
      drillDownId: null,
      drillDownName: '',
      drillDownTransactions: [],
      drillDownPaymentStats: [],
      drillDownSalespersonStats: [],
      drillDownLocationStats: [],
      drillDownPieChart: null,
      
      dateRange: [],
      filters: {
        location: null,
        salesperson: null,
        paymentMethod: null,
        saleType: null
      },
      
      kpiData: {
        // Net Sales (what we display on cards)
        today: 0,
        yesterday: 0,
        this_week: 0,
        last_week: 0,
        this_month: 0,
        last_month: 0,
        today_count: 0,
        yesterday_count: 0,
        this_week_count: 0,
        last_week_count: 0,
        this_month_count: 0,
        last_month_count: 0,
        this_week_change: null,
        this_month_change: null,
        yesterday_change: null,
        today_change: null,
        // Gross Sales (for reference)
        today_gross: 0,
        yesterday_gross: 0,
        this_week_gross: 0,
        last_week_gross: 0,
        this_month_gross: 0,
        last_month_gross: 0
      },
      
      periodPaymentData: {},
      paymentBreakdownData: [],
      
      rawSummaryData: null,
      rawLocationSummary: {},
      rawSalespersonSummary: {},
      transactions: [],
      filteredTransactions: [],
      
      currentPage: 1,
      pageSize: 25,
      
      pieChart: null,
      periodDonuts: {},
      
      packet: { userDetails: {}, locations: [] },
      
      colors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#f472b6', '#34d399'],
      
      periodMap: {
        today: 'Today',
        yesterday: 'Yesterday',
        this_week: 'This Week',
        last_week: 'Last Week',
        this_month: 'This Month',
        last_month: 'Last Month'
      },
      
      financeMethods: ['ACIMA CREDIT', 'AMERICAN FIRST', 'WELLS FARGO', 'SNAP FINANCE', 'PROGRESSIVE', 'CHECK PERSONAL'],
      taxExemptMethods: ['TAX EXEMPT']
    };
  },
  
  computed: {
    formattedDateRange() {
      if (!this.dateRange || this.dateRange.length < 2) return 'Select dates';
      return `${this.dateRange[0]} - ${this.dateRange[1]}`;
    },
    
    hasData() {
      return this.rawSummaryData && this.rawSummaryData.total_sales > 0;
    },
    
    hasActiveFilters() {
      return this.filters.location !== null || 
             this.filters.salesperson !== null || 
             this.filters.paymentMethod !== null ||
             this.filters.saleType !== null;
    },
    
    carouselSlides() {
      // Only 3 slides now - removed payment methods slide
      return [
        { id: 'location', label: 'By Location' },
        { id: 'salesperson', label: 'By Salesperson' },
        { id: 'transactions', label: 'Transactions' }
      ];
    },
    
    summaryItems() {
      return [
        { icon: '💰', label: 'Total Sales', value: this.formatCurrency(this.modalSummary.totalSales) },
        { icon: '📋', label: 'Transactions', value: this.modalSummary.transactions },
        { icon: '📈', label: 'Net Sales', value: this.formatCurrency(this.modalSummary.netSales) },
        { icon: '📊', label: 'Avg Order', value: this.formatCurrency(this.modalSummary.avgOrder) }
      ];
    },
    
    drillDownSummaryItems() {
      return [
        { icon: '💰', label: 'Total Sales', value: this.formatCurrency(this.drillDownSummary.totalSales) },
        { icon: '📋', label: 'Transactions', value: this.drillDownSummary.transactions },
        { icon: '📈', label: 'Net Sales', value: this.formatCurrency(this.drillDownSummary.netSales) },
        { icon: '📊', label: 'Avg Order', value: this.formatCurrency(this.drillDownSummary.avgOrder) }
      ];
    },
    
    summaryCards() {
      return [
        { icon: '💰', label: 'Total Sales', value: this.formatCurrency(this.filteredSummaryData.total_sales), iconClass: 'total-sales' },
        { icon: '📈', label: 'Net Sales', value: this.formatCurrency(this.filteredSummaryData.total_net_sales), iconClass: 'net-sales' },
        { icon: '🛒', label: 'Orders', value: this.filteredSummaryData.total_orders, iconClass: 'orders' },
        { icon: '📊', label: 'Avg Order', value: this.formatCurrency(this.filteredSummaryData.total_net_sales / (this.filteredSummaryData.total_orders || 1)), iconClass: 'avg-order' }
      ];
    },
    
    drillDownTitle() {
      if (this.drillDownType === 'location') {
        return `📍 ${this.drillDownName} - Sales Details`;
      } else if (this.drillDownType === 'salesperson') {
        return `👤 ${this.drillDownName} - Sales Details`;
      }
      return 'Details';
    },
    
    drillDownSummary() {
      if (!this.drillDownTransactions || !this.drillDownTransactions.length) {
        return { totalSales: 0, transactions: 0, netSales: 0, avgOrder: 0 };
      }
      
      let totalSales = 0;
      let netSales = 0;
      this.drillDownTransactions.forEach(t => {
        totalSales += Math.abs(t.total_dollar_amount || 0);
        netSales += (t.total_dollar_amount || 0);
      });
      
      return {
        totalSales: totalSales,
        transactions: this.drillDownTransactions.length,
        netSales: netSales,
        avgOrder: this.drillDownTransactions.length > 0 ? netSales / this.drillDownTransactions.length : 0
      };
    },
    
    drillDownTotalSalespersonSales() {
      return this.drillDownSalespersonStats.reduce((sum, p) => sum + p.sales, 0);
    },
    
    drillDownTotalSalespersonOrders() {
      return this.drillDownSalespersonStats.reduce((sum, p) => sum + p.orders, 0);
    },
    
    drillDownTotalSalespersonAvg() {
      const total = this.drillDownTotalSalespersonSales;
      const orders = this.drillDownTotalSalespersonOrders;
      return orders > 0 ? total / orders : 0;
    },
    
    drillDownTotalLocationSales() {
      return this.drillDownLocationStats.reduce((sum, l) => sum + l.sales, 0);
    },
    
    drillDownTotalLocationOrders() {
      return this.drillDownLocationStats.reduce((sum, l) => sum + l.orders, 0);
    },
    
    drillDownTotalLocationAvg() {
      const total = this.drillDownTotalLocationSales;
      const orders = this.drillDownTotalLocationOrders;
      return orders > 0 ? total / orders : 0;
    },
    
    locationOptions() {
      if (!this.packet.locations) return [];
      return this.packet.locations.map(l => ({ value: l.value, text: l.text }));
    },
    
    salespersonOptions() {
      if (!this.rawSalespersonSummary) return [];
      return Object.keys(this.rawSalespersonSummary).map(name => ({ value: name, text: name }));
    },
    
    paymentMethodOptions() {
      if (!this.transactions || !this.transactions.length) return [];
      const methods = new Set();
      this.transactions.forEach(t => {
        if (t.payment_methods && t.payment_methods.length) {
          t.payment_methods.forEach(m => {
            let cleanMethod = m;
            if (m.includes('FINANCE - ') || m.toUpperCase().includes('FINANCE')) {
              cleanMethod = m.replace(/FINANCE\s*-\s*/i, '').trim();
              if (this.financeMethods.some(f => cleanMethod.toUpperCase().includes(f))) {
                methods.add('FINANCE');
                return;
              }
            }
            
            if (this.financeMethods.some(f => m.toUpperCase().includes(f))) {
              methods.add('FINANCE');
            } else if (this.taxExemptMethods.some(t => m.toUpperCase().includes(t))) {
              methods.add('TAX EXEMPT');
            } else {
              methods.add(cleanMethod);
            }
          });
        }
      });
      return Array.from(methods).map(m => ({ value: m, text: m }));
    },
    
    saleTypeOptions() {
      return [
        { value: null, text: 'All Types' },
        { value: 'regular', text: 'Regular Sales' },
        { value: 'layaway', text: 'Layaway' },
        { value: 'refund', text: 'Refunds' }
      ];
    },
    
    activeFilterCount() {
      return Object.values(this.filters).filter(v => v !== null).length;
    },
    
    activePeriodLabel() {
      return this.activePeriod ? this.periodMap[this.activePeriod] || this.activePeriod : '';
    },
    
    activePeriodValue() {
      return this.activePeriod ? this.kpiData[this.activePeriod] || 0 : 0;
    },
    
    activePeriodCount() {
      return this.activePeriod ? this.kpiData[`${this.activePeriod}_count`] || 0 : 0;
    },
    
    modalTitle() {
      return this.modalPeriod ? `${this.periodMap[this.modalPeriod] || this.modalPeriod} - Sales Details` : 'Period Details';
    },
    
    modalSummary() {
      if (!this.modalTransactions || !this.modalTransactions.length) {
        return { totalSales: 0, transactions: 0, netSales: 0, avgOrder: 0 };
      }
      
      let totalSales = 0;
      let netSales = 0;
      this.modalTransactions.forEach(t => {
        totalSales += Math.abs(t.total_dollar_amount || 0);
        netSales += (t.total_dollar_amount || 0);
      });
      
      return {
        totalSales: totalSales,
        transactions: this.modalTransactions.length,
        netSales: netSales,
        avgOrder: this.modalTransactions.length > 0 ? netSales / this.modalTransactions.length : 0
      };
    },
    
    modalTotalLocationSales() {
      return this.modalLocationStats.reduce((sum, l) => sum + l.sales, 0);
    },
    
    modalTotalLocationNet() {
      return this.modalLocationStats.reduce((sum, l) => sum + l.net, 0);
    },
    
    modalTotalLocationOrders() {
      return this.modalLocationStats.reduce((sum, l) => sum + l.orders, 0);
    },
    
    modalTotalLocationAvg() {
      const total = this.modalTotalLocationNet;
      const orders = this.modalTotalLocationOrders;
      return orders > 0 ? total / orders : 0;
    },
    
    modalTotalSalespersonSales() {
      return this.modalSalespersonStats.reduce((sum, p) => sum + p.sales, 0);
    },
    
    modalTotalSalespersonNet() {
      return this.modalSalespersonStats.reduce((sum, p) => sum + p.net, 0);
    },
    
    modalTotalSalespersonOrders() {
      return this.modalSalespersonStats.reduce((sum, p) => sum + p.orders, 0);
    },
    
    modalTotalSalespersonAvg() {
      const total = this.modalTotalSalespersonNet;
      const orders = this.modalTotalSalespersonOrders;
      return orders > 0 ? total / orders : 0;
    },
    
    modalPaginatedTransactions() {
      const start = (this.modalCurrentPage - 1) * this.modalPageSize;
      return this.modalTransactions.slice(start, start + this.modalPageSize);
    },
    
    periodTabs() {
      const d = this.kpiData;
      
      return [
        { key: 'today', label: 'Today', value: d.today || 0, count: d.today_count || 0, change: d.today_change },
        { key: 'this_week', label: 'This Week', value: d.this_week || 0, count: d.this_week_count || 0, change: d.this_week_change },
        { key: 'this_month', label: 'This Month', value: d.this_month || 0, count: d.this_month_count || 0, change: d.this_month_change },
        { key: 'yesterday', label: 'Yesterday', value: d.yesterday || 0, count: d.yesterday_count || 0, change: d.yesterday_change },
        { key: 'last_week', label: 'Last Week', value: d.last_week || 0, count: d.last_week_count || 0, change: d.last_week_change },
        { key: 'last_month', label: 'Last Month', value: d.last_month || 0, count: d.last_month_count || 0, change: d.last_month_change }
      ];
    },
    
    filteredSummaryData() {
      if (!this.filteredTransactions || !this.filteredTransactions.length) {
        return {
          total_sales: 0,
          total_net_sales: 0,
          total_orders: 0
        };
      }
      
      let totalSales = 0;
      let totalNet = 0;
      
      this.filteredTransactions.forEach(t => {
        const amount = Math.abs(t.total_dollar_amount || 0);
        totalSales += amount;
        totalNet += (t.total_dollar_amount || 0);
      });
      
      return {
        total_sales: totalSales,
        total_net_sales: totalNet,
        total_orders: this.filteredTransactions.length
      };
    },
    
    filteredLocationStats() {
      if (!this.filteredTransactions || !this.filteredTransactions.length) return [];
      
      const locationMap = {};
      this.filteredTransactions.forEach(t => {
        const id = t.location_id;
        if (!locationMap[id]) {
          locationMap[id] = {
            sales: 0,
            net: 0,
            orders: 0
          };
        }
        locationMap[id].sales += Math.abs(t.total_dollar_amount || 0);
        locationMap[id].net += (t.total_dollar_amount || 0);
        locationMap[id].orders += 1;
      });
      
      const totalNet = Object.values(locationMap).reduce((sum, l) => sum + l.net, 0) || 1;
      
      return Object.keys(locationMap).map(id => ({
        location_id: id,
        location_name: this.getLocationName(id),
        sales: locationMap[id].sales,
        net: locationMap[id].net,
        orders: locationMap[id].orders,
        avg_order: locationMap[id].orders > 0 ? locationMap[id].net / locationMap[id].orders : 0,
        percentage: ((locationMap[id].net || 0) / totalNet * 100).toFixed(1)
      })).sort((a, b) => b.sales - a.sales);
    },
    
    filteredTotalLocationSales() {
      return this.filteredLocationStats.reduce((sum, l) => sum + l.sales, 0);
    },
    
    filteredTotalLocationNet() {
      return this.filteredLocationStats.reduce((sum, l) => sum + l.net, 0);
    },
    
    filteredTotalLocationOrders() {
      return this.filteredLocationStats.reduce((sum, l) => sum + l.orders, 0);
    },
    
    filteredSalespersonStats() {
      if (!this.filteredTransactions || !this.filteredTransactions.length) return [];
      
      const personMap = {};
      this.filteredTransactions.forEach(t => {
        const name = t.username || 'Unknown';
        if (!personMap[name]) {
          personMap[name] = {
            sales: 0,
            net: 0,
            orders: 0
          };
        }
        personMap[name].sales += Math.abs(t.total_dollar_amount || 0);
        personMap[name].net += (t.total_dollar_amount || 0);
        personMap[name].orders += 1;
      });
      
      const totalNet = Object.values(personMap).reduce((sum, p) => sum + p.net, 0) || 1;
      
      return Object.keys(personMap).map(name => ({
        salesperson: name,
        sales: personMap[name].sales,
        net: personMap[name].net,
        orders: personMap[name].orders,
        avg_order: personMap[name].orders > 0 ? personMap[name].net / personMap[name].orders : 0,
        percentage: ((personMap[name].net || 0) / totalNet * 100).toFixed(1)
      })).sort((a, b) => b.sales - a.sales);
    },
    
    filteredTotalSalespersonSales() {
      return this.filteredSalespersonStats.reduce((sum, p) => sum + p.sales, 0);
    },
    
    filteredTotalSalespersonNet() {
      return this.filteredSalespersonStats.reduce((sum, p) => sum + p.net, 0);
    },
    
    filteredTotalSalespersonOrders() {
      return this.filteredSalespersonStats.reduce((sum, p) => sum + p.orders, 0);
    },
    
    filteredTotalSalespersonAvgOrder() {
      const totalNet = this.filteredTotalSalespersonNet;
      const totalOrders = this.filteredTotalSalespersonOrders;
      return totalOrders > 0 ? totalNet / totalOrders : 0;
    },
    
    paymentMethodStats() {
      if (!this.transactions || !this.transactions.length) return [];
      
      const paymentTotals = {};
      const paymentCounts = {};
      const subTotals = {};
      
      const transactionsToUse = this.filteredTransactions.length ? this.filteredTransactions : this.transactions;
      
      transactionsToUse.forEach(transaction => {
        if (!transaction.payment_methods || !transaction.payment_methods.length) return;
        
        transaction.payment_methods.forEach(method => {
          if (!method) return;
          
          const amount = Math.abs(transaction.total_dollar_amount || 0);
          
          let cleanMethod = method;
          let isFinance = false;
          
          const methodUpper = method.toUpperCase();
          if (methodUpper.includes('ACIMA CREDIT') || 
              methodUpper.includes('AMERICAN FIRST') || 
              methodUpper.includes('WELLS FARGO') || 
              methodUpper.includes('SNAP FINANCE') || 
              methodUpper.includes('PROGRESSIVE') || 
              methodUpper.includes('CHECK PERSONAL') ||
              methodUpper.includes('FINANCE')) {
            isFinance = true;
            cleanMethod = method.replace(/FINANCE\s*-\s*/i, '').trim();
            if (!cleanMethod || cleanMethod === method) {
              cleanMethod = method.replace(/FINANCE/i, '').trim();
            }
          }
          
          if (methodUpper.includes('TAX EXEMPT')) {
            const groupKey = 'TAX EXEMPT';
            if (!paymentTotals[groupKey]) {
              paymentTotals[groupKey] = 0;
              paymentCounts[groupKey] = 0;
              subTotals[groupKey] = {};
            }
            paymentTotals[groupKey] += amount;
            paymentCounts[groupKey] += 1;
            if (!subTotals[groupKey][method]) {
              subTotals[groupKey][method] = 0;
            }
            subTotals[groupKey][method] += amount;
          } 
          else if (isFinance) {
            const groupKey = 'FINANCE';
            if (!paymentTotals[groupKey]) {
              paymentTotals[groupKey] = 0;
              paymentCounts[groupKey] = 0;
              subTotals[groupKey] = {};
            }
            paymentTotals[groupKey] += amount;
            paymentCounts[groupKey] += 1;
            if (!subTotals[groupKey][cleanMethod]) {
              subTotals[groupKey][cleanMethod] = 0;
            }
            subTotals[groupKey][cleanMethod] += amount;
          } 
          else {
            if (!paymentTotals[method]) {
              paymentTotals[method] = 0;
              paymentCounts[method] = 0;
            }
            paymentTotals[method] += amount;
            paymentCounts[method] += 1;
          }
        });
      });
      
      const total = Object.values(paymentTotals).reduce((s, v) => s + v, 0) || 1;
      
      const result = Object.keys(paymentTotals).map(method => {
        const subMethods = [];
        if (subTotals[method]) {
          Object.keys(subTotals[method]).forEach(sub => {
            subMethods.push({
              name: sub,
              amount: subTotals[method][sub]
            });
          });
        }
        return {
          method: method,
          amount: paymentTotals[method],
          count: paymentCounts[method] || 1,
          percentage: (paymentTotals[method] / total * 100).toFixed(1),
          subMethods: subMethods.sort((a, b) => b.amount - a.amount)
        };
      }).sort((a, b) => b.amount - a.amount);
      
      return result;
    },
    
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredTransactions.slice(start, start + this.pageSize);
    }
  },
  
  watch: {
    paymentMethodStats: {
      handler() {
        this.$nextTick(() => {
          this.renderPieChart();
        });
      },
      deep: true
    },
    
    periodPaymentData: {
      handler() {
        this.$nextTick(() => {
          this.renderPeriodDonuts();
        });
      },
      deep: true
    },
    
    showPeriodModal(val) {
      if (val) {
        this.$nextTick(() => {
          this.renderModalPieChart();
        });
      }
    },
    
    showDrillDownModal(val) {
      if (val) {
        this.$nextTick(() => {
          this.renderDrillDownPieChart();
        });
      }
    }
  },
  
  mounted() {
    this.loadUserData();
    this.setDefaultDateRange();
    this.$nextTick(() => {
     this.fetchAllData();
    });
  },
  
  beforeDestroy() {
    if (this.pieChart) this.pieChart.destroy();
    if (this.modalPieChart) this.modalPieChart.destroy();
    if (this.drillDownPieChart) this.drillDownPieChart.destroy();
    Object.values(this.periodDonuts).forEach(chart => {
      if (chart) chart.destroy();
    });
  },
  
  methods: {
    // Drill Down Methods
    drillDownLocation(item) {
      this.drillDownType = 'location';
      this.drillDownId = item.location_id;
      this.drillDownName = item.location_name;
      this.drillDownTransactions = this.modalTransactions.filter(t => t.location_id == item.location_id);
      this.drillDownPaymentStats = this.calculatePaymentStats(this.drillDownTransactions);
      this.drillDownSalespersonStats = this.calculateSalespersonStats(this.drillDownTransactions);
      this.drillDownLocationStats = [];
      this.showDrillDownModal = true;
      this.isDrillDownLoading = false;
    },
    
    drillDownSalesperson(item) {
      this.drillDownType = 'salesperson';
      this.drillDownId = item.salesperson;
      this.drillDownName = item.salesperson;
      this.drillDownTransactions = this.modalTransactions.filter(t => t.username === item.salesperson);
      this.drillDownPaymentStats = this.calculatePaymentStats(this.drillDownTransactions);
      this.drillDownLocationStats = this.calculateLocationStats(this.drillDownTransactions);
      this.drillDownSalespersonStats = [];
      this.showDrillDownModal = true;
      this.isDrillDownLoading = false;
    },
    
    onDrillDownClose() {
      if (this.drillDownPieChart) {
        this.drillDownPieChart.destroy();
        this.drillDownPieChart = null;
      }
    },
    
    renderDrillDownPieChart() {
      const canvas = document.getElementById('drillDownPieChart');
      if (!canvas) return;
      
      if (this.drillDownPieChart) {
        this.drillDownPieChart.destroy();
        this.drillDownPieChart = null;
      }
      
      const stats = this.drillDownPaymentStats;
      if (!stats || !stats.length) {
        this.drillDownPieChart = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['No Data'],
            datasets: [{
              data: [1],
              backgroundColor: ['#e2e8f0']
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            cutout: '60%'
          }
        });
        return;
      }
      
      const labels = stats.map(s => s.method);
      const data = stats.map(s => s.amount);
      const colors = this.colors.slice(0, stats.length);
      
      this.drillDownPieChart = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: colors,
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: { size: 12 },
                padding: 12,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                  return `${ctx.label}: $${ctx.parsed.toLocaleString()} (${percentage}%)`;
                }
              }
            }
          },
          cutout: '60%'
        }
      });
    },
    
    // Carousel navigation with buttons only (no mouse scroll)
    nextSlide() {
      if (this.currentSlide < this.carouselSlides.length - 1) {
        this.currentSlide++;
      }
    },
    
    prevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
      }
    },
    
    getDateRange(period) {
      const now = moment();
      let start, end;
      switch(period) {
        case 'today': 
          start = now.format('MM-DD-YYYY'); 
          end = now.format('MM-DD-YYYY'); 
          break;
        case 'yesterday': 
          start = now.clone().subtract(1, 'day').format('MM-DD-YYYY'); 
          end = now.clone().subtract(1, 'day').format('MM-DD-YYYY'); 
          break;
        case 'this_week': 
          start = now.clone().startOf('week').format('MM-DD-YYYY'); 
          end = now.clone().endOf('week').format('MM-DD-YYYY'); 
          break;
        case 'last_week': 
          start = now.clone().subtract(1, 'week').startOf('week').format('MM-DD-YYYY'); 
          end = now.clone().subtract(1, 'week').endOf('week').format('MM-DD-YYYY'); 
          break;
        case 'this_month': 
          start = now.clone().startOf('month').format('MM-DD-YYYY'); 
          end = now.clone().endOf('month').format('MM-DD-YYYY'); 
          break;
        case 'last_month': 
          start = now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY'); 
          end = now.clone().subtract(1, 'month').endOf('month').format('MM-DD-YYYY'); 
          break;
        default: return null;
      }
      return [start, end];
    },
    
    async openPeriodModal(key) {
      this.modalPeriod = key;
      this.currentSlide = 0;
      this.showPeriodModal = true;
      this.isModalLoading = true;
      this.modalCurrentPage = 1;
      
      try {
        const dateRange = this.getDateRange(key);
        if (!dateRange) return;
        
        const data = {
          username: this.packet.userDetails.username,
          dates: dateRange,
          location_id: null,
          salesperson: null,
          current_user_id: this.packet.userDetails.id,
          userid: this.packet.userDetails.id
        };
        
        const response = await axios.post('/api/reports/reports_v3/', data, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data.result && !response.data.error) {
          const transactions = response.data.result.map(item => ({
            ...item,
            payment_methods: item.payment_methods_with_amounts ?
              item.payment_methods_with_amounts.map(p => p.method) :
              item.payment_methods || []
          }));
          
          this.modalTransactions = transactions;
          this.modalPaymentStats = this.calculatePaymentStats(transactions);
          this.modalLocationStats = this.calculateLocationStats(transactions);
          this.modalSalespersonStats = this.calculateSalespersonStats(transactions);
        }
      } catch (error) {
        console.error('Error fetching period data:', error);
        this.$bvToast.toast('Error loading period data', { title: 'Error', variant: 'danger' });
      } finally {
        this.isModalLoading = false;
      }
    },
    
    onModalClose() {
      if (this.modalPieChart) {
        this.modalPieChart.destroy();
        this.modalPieChart = null;
      }
    },
    
    calculatePaymentStats(transactions) {
      if (!transactions || !transactions.length) return [];
      
      const paymentTotals = {};
      const paymentCounts = {};
      
      transactions.forEach(transaction => {
        if (!transaction.payment_methods || !transaction.payment_methods.length) return;
        
        transaction.payment_methods.forEach(method => {
          if (!method) return;
          const amount = Math.abs(transaction.total_dollar_amount || 0);
          
          let cleanMethod = method;
          let isFinance = false;
          
          const methodUpper = method.toUpperCase();
          if (methodUpper.includes('ACIMA CREDIT') || 
              methodUpper.includes('AMERICAN FIRST') || 
              methodUpper.includes('WELLS FARGO') || 
              methodUpper.includes('SNAP FINANCE') || 
              methodUpper.includes('PROGRESSIVE') || 
              methodUpper.includes('CHECK PERSONAL') ||
              methodUpper.includes('FINANCE')) {
            isFinance = true;
            cleanMethod = method.replace(/FINANCE\s*-\s*/i, '').trim();
            if (!cleanMethod || cleanMethod === method) {
              cleanMethod = method.replace(/FINANCE/i, '').trim();
            }
          }
          
          let groupKey = method;
          if (methodUpper.includes('TAX EXEMPT')) {
            groupKey = 'TAX EXEMPT';
          } else if (isFinance) {
            groupKey = 'FINANCE';
          }
          
          if (!paymentTotals[groupKey]) {
            paymentTotals[groupKey] = 0;
            paymentCounts[groupKey] = 0;
          }
          paymentTotals[groupKey] += amount;
          paymentCounts[groupKey] += 1;
        });
      });
      
      const total = Object.values(paymentTotals).reduce((s, v) => s + v, 0) || 1;
      
      return Object.keys(paymentTotals).map(method => ({
        method: method,
        amount: paymentTotals[method],
        count: paymentCounts[method] || 0,
        percentage: (paymentTotals[method] / total * 100).toFixed(1)
      })).sort((a, b) => b.amount - a.amount);
    },
    
    calculateLocationStats(transactions) {
      if (!transactions || !transactions.length) return [];
      
      const locationMap = {};
      transactions.forEach(t => {
        const id = t.location_id;
        if (!locationMap[id]) {
          locationMap[id] = {
            sales: 0,
            net: 0,
            orders: 0
          };
        }
        locationMap[id].sales += Math.abs(t.total_dollar_amount || 0);
        locationMap[id].net += (t.total_dollar_amount || 0);
        locationMap[id].orders += 1;
      });
      
      const totalNet = Object.values(locationMap).reduce((sum, l) => sum + l.net, 0) || 1;
      
      return Object.keys(locationMap).map(id => ({
        location_id: id,
        location_name: this.getLocationName(id),
        sales: locationMap[id].sales,
        net: locationMap[id].net,
        orders: locationMap[id].orders,
        avg_order: locationMap[id].orders > 0 ? locationMap[id].net / locationMap[id].orders : 0,
        percentage: ((locationMap[id].net || 0) / totalNet * 100).toFixed(1)
      })).sort((a, b) => b.sales - a.sales);
    },
    
    calculateSalespersonStats(transactions) {
      if (!transactions || !transactions.length) return [];
      
      const personMap = {};
      transactions.forEach(t => {
        const name = t.username || 'Unknown';
        if (!personMap[name]) {
          personMap[name] = {
            sales: 0,
            net: 0,
            orders: 0
          };
        }
        personMap[name].sales += Math.abs(t.total_dollar_amount || 0);
        personMap[name].net += (t.total_dollar_amount || 0);
        personMap[name].orders += 1;
      });
      
      const totalNet = Object.values(personMap).reduce((sum, p) => sum + p.net, 0) || 1;
      
      return Object.keys(personMap).map(name => ({
        salesperson: name,
        sales: personMap[name].sales,
        net: personMap[name].net,
        orders: personMap[name].orders,
        avg_order: personMap[name].orders > 0 ? personMap[name].net / personMap[name].orders : 0,
        percentage: ((personMap[name].net || 0) / totalNet * 100).toFixed(1)
      })).sort((a, b) => b.sales - a.sales);
    },
    
    renderModalPieChart() {
      const canvas = document.getElementById('modalPieChart');
      if (!canvas) return;
      
      if (this.modalPieChart) {
        this.modalPieChart.destroy();
        this.modalPieChart = null;
      }
      
      const stats = this.modalPaymentStats;
      if (!stats || !stats.length) {
        this.modalPieChart = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['No Data'],
            datasets: [{
              data: [1],
              backgroundColor: ['#e2e8f0']
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            cutout: '60%'
          }
        });
        return;
      }
      
      const labels = stats.map(s => s.method);
      const data = stats.map(s => s.amount);
      const colors = this.colors.slice(0, stats.length);
      
      this.modalPieChart = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: colors,
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: { size: 12 },
                padding: 12,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                  return `${ctx.label}: $${ctx.parsed.toLocaleString()} (${percentage}%)`;
                }
              }
            }
          },
          cutout: '60%'
        }
      });
    },
    
    async fetchKPI(period) {
      const dateRange = this.getDateRange(period);
      if (!dateRange) return null;
      
      const data = {
        username: this.packet.userDetails.username,
        dates: dateRange,
        location_id: this.filters.location,
        salesperson: this.filters.salesperson,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };
      
      try {
        const response = await axios.post('/api/reports/reports_optimized/', data, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data.result && !response.data.error) {
          const result = response.data.result;
          const summary = result.summary || {};
          
          // Get NET SALES from the summary
          const netSales = summary.total_net_sales || 0;
          const grossSales = summary.total_sales || 0;
          const totalOrders = summary.total_orders || 0;
          
          const paymentBreakdown = result.payment_breakdown || {};
          const paymentStats = Object.keys(paymentBreakdown).map(key => ({
            method: key,
            amount: paymentBreakdown[key].amount,
            count: paymentBreakdown[key].count,
            percentage: 0
          }));
          
          const total = paymentStats.reduce((sum, p) => sum + p.amount, 0);
          if (total > 0) {
            paymentStats.forEach(p => {
              p.percentage = ((p.amount / total) * 100).toFixed(1);
            });
          }
          
          return {
            netSales: netSales,
            grossSales: grossSales,
            count: totalOrders,
            paymentStats: paymentStats,
            transactions: result.sales_data || []
          };
        }
        return { netSales: 0, grossSales: 0, count: 0, paymentStats: [], transactions: [] };
      } catch (error) {
        console.error(`Error fetching ${period}:`, error);
        return { netSales: 0, grossSales: 0, count: 0, paymentStats: [], transactions: [] };
      }
    },
    
   async fetchAllKPIs() {
        // Single API call for all periods
        const data = {
            username: this.packet.userDetails.username,
            location_id: this.filters.location,
            salesperson: this.filters.salesperson,
            current_user_id: this.packet.userDetails.id,
            userid: this.packet.userDetails.id,
            fetch_all_periods: true  // NEW: flag to fetch all periods
        };
        
        try {
            this.isLoading = true;
            const response = await axios.post('/api/reports/reports_optimized/', data, {
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (response.data.result && !response.data.error) {
                const allPeriods = response.data.result;
                
                // Map the data to kpiData
                const periods = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
                
                periods.forEach(period => {
                    const periodData = allPeriods[period] || { netSales: 0, count: 0, grossSales: 0 };
                    
                    this.kpiData[period] = periodData.netSales || 0;
                    this.kpiData[`${period}_count`] = periodData.count || 0;
                    this.kpiData[`${period}_gross`] = periodData.grossSales || 0;
                    
                    // Store payment stats for period donuts
                    this.periodPaymentData[period] = periodData.paymentStats || [];
                });
                
                // Set changes
                this.kpiData.this_week_change = allPeriods.this_week_change !== undefined ? allPeriods.this_week_change : null;
                this.kpiData.this_month_change = allPeriods.this_month_change !== undefined ? allPeriods.this_month_change : null;
                this.kpiData.yesterday_change = allPeriods.yesterday_change !== undefined ? allPeriods.yesterday_change : null;
                this.kpiData.today_change = allPeriods.today_change !== undefined ? allPeriods.today_change : null;
                
                // Render period donuts
                this.$nextTick(() => {
                    this.renderPeriodDonuts();
                });
            }
        } catch (error) {
            console.error('Error fetching all KPIs:', error);
            this.$bvToast.toast('Error loading period data', { title: 'Error', variant: 'danger' });
        } finally {
            this.isLoading = false;
        }
    },
    
    renderPeriodDonuts() {
      const periods = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
      
      periods.forEach(period => {
        const canvas = document.getElementById('donut_' + period);
        if (!canvas) {
          console.warn('Canvas not found for period:', period);
          return;
        }
        
        if (this.periodDonuts[period]) {
          this.periodDonuts[period].destroy();
          delete this.periodDonuts[period];
        }
        
        const data = this.periodPaymentData[period] || [];
        
        if (!data.length) {
          this.periodDonuts[period] = new Chart(canvas, {
            type: 'doughnut',
            data: {
              labels: ['No Data'],
              datasets: [{
                data: [1],
                backgroundColor: ['#e2e8f0']
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false }
              },
              cutout: '70%'
            }
          });
          return;
        }
        
        const topData = data.slice(0, 5);
        const labels = topData.map(d => d.method.length > 12 ? d.method.substring(0, 10) + '…' : d.method);
        const values = topData.map(d => d.amount);
        const colors = this.colors.slice(0, topData.length);
        
        this.periodDonuts[period] = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: labels,
            datasets: [{
              data: values,
              backgroundColor: colors,
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  font: { size: 8 },
                  padding: 4,
                  usePointStyle: true,
                  pointStyle: 'circle',
                  boxWidth: 8
                }
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                    const label = ctx.label || '';
                    return `${label}: $${ctx.parsed.toFixed(2)} (${percentage}%)`;
                  }
                }
              }
            },
            cutout: '65%'
          }
        });
      });
    },
    
      async fetchMainData() {
        if (!this.dateRange || this.dateRange.length < 2) {
            this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
            return;
        }
        
        this.isLoading = true;
        this.showDetailed = false;
        
        try {
            await this.fetchSummaryData();
            await this.fetchDetailedData();
            this.applyFilters();
            
            this.$nextTick(() => {
                this.renderPieChart();
            });
        } catch (error) {
            console.error('Error:', error);
            this.$bvToast.toast('Error fetching data', { title: 'Error', variant: 'danger' });
        } finally {
            this.isLoading = false;
        }
    },
    
    
     async fetchSummaryData() {
        const data = {
            username: this.packet.userDetails.username,
            dates: this.dateRange,
            location_id: this.filters.location,
            salesperson: this.filters.salesperson,
            current_user_id: this.packet.userDetails.id,
            userid: this.packet.userDetails.id
        };
        
        const response = await axios.post('/api/reports/reports_optimized/', data, {
            headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data.result && !response.data.error) {
            const result = response.data.result;
            this.rawSummaryData = result.summary || {};
            this.rawLocationSummary = result.location_summary || {};
            this.rawSalespersonSummary = result.salesperson_summary || {};
            
            const paymentBreakdown = result.payment_breakdown || {};
            this.paymentBreakdownData = Object.keys(paymentBreakdown).map(key => ({
                method: key,
                amount: paymentBreakdown[key].amount,
                count: paymentBreakdown[key].count
            }));
        }
    },
    
     async fetchDetailedData() {
        const data = {
            username: this.packet.userDetails.username,
            dates: this.dateRange,
            location_id: this.filters.location,
            salesperson: this.filters.salesperson,
            current_user_id: this.packet.userDetails.id,
            userid: this.packet.userDetails.id
        };
        
        const response = await axios.post('/api/reports/reports_v3/', data, {
            headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data.result && !response.data.error) {
            this.transactions = response.data.result.map(item => ({
                ...item,
                payment_methods: item.payment_methods_with_amounts ?
                    item.payment_methods_with_amounts.map(p => p.method) :
                    item.payment_methods || []
            }));
        } else {
            this.transactions = [];
        }
    },
    
    async fetchAllData() {
        this.isLoading = true;
        try {
            // First fetch all periods in one call
            await this.fetchAllKPIs();
            
            // Then fetch detailed data for the selected period
            if (this.activePeriod) {
                await this.fetchMainData();
            }
        } catch (error) {
            console.error('Error fetching all data:', error);
            this.$bvToast.toast('Error loading data', { title: 'Error', variant: 'danger' });
        } finally {
            this.isLoading = false;
        }
    },
  selectPeriod(key) {
        if (this.activePeriod === key) {
            this.clearPeriodFilter();
            return;
        }
        
        this.activePeriod = key;
        const dateRange = this.getDateRange(key);
        if (dateRange) {
            this.dateRange = dateRange;
            this.fetchAllData();
        }
    },
    
   clearPeriodFilter() {
        this.activePeriod = null;
        this.setDefaultDateRange();
        this.rawSummaryData = null;
        this.rawLocationSummary = {};
        this.rawSalespersonSummary = {};
        this.transactions = [];
        this.filteredTransactions = [];
        this.kpiData = {
            today: 0, yesterday: 0, this_week: 0, last_week: 0, this_month: 0, last_month: 0,
            today_count: 0, yesterday_count: 0, this_week_count: 0, last_week_count: 0,
            this_month_count: 0, last_month_count: 0, this_week_change: null, this_month_change: null,
            yesterday_change: null, today_change: null,
            today_gross: 0, yesterday_gross: 0, this_week_gross: 0, last_week_gross: 0,
            this_month_gross: 0, last_month_gross: 0
        };
        this.periodPaymentData = {};
        this.filters = {
            location: null,
            salesperson: null,
            paymentMethod: null,
            saleType: null
        };
        this.showDetailed = false;
        this.fetchAllKPIs(); // Now this is a single API call
    },
    
    
    isPaymentMethodActive(method) {
      if (this.filters.paymentMethod === method) return true;
      if (method === 'FINANCE' && this.financeMethods.includes(this.filters.paymentMethod)) return true;
      if (method === 'TAX EXEMPT' && this.taxExemptMethods.includes(this.filters.paymentMethod)) return true;
      return false;
    },
    
    drillDownPaymentMethod(item) {
      if (item.method === 'FINANCE' || item.method === 'TAX EXEMPT') {
        this.drillDown('paymentMethod', item.method);
      } else {
        this.drillDown('paymentMethod', item.method);
      }
    },
    
    drillDown(type, value) {
      if (type === 'location') {
        this.filters.location = this.filters.location === value ? null : value;
        this.filters.salesperson = null;
        this.filters.paymentMethod = null;
      } else if (type === 'salesperson') {
        this.filters.salesperson = this.filters.salesperson === value ? null : value;
        this.filters.location = null;
        this.filters.paymentMethod = null;
      } else if (type === 'paymentMethod') {
        this.filters.paymentMethod = this.filters.paymentMethod === value ? null : value;
      }
      
      if (this.activePeriod) {
        this.fetchMainData();
      }
      
      if (this.hasActiveFilters) {
        this.showDetailed = true;
      } else {
        this.showDetailed = false;
      }
    },
    
    onFilterChange() {
      if (this.activePeriod) {
        this.fetchMainData();
      }
    },
    
    applyFilters() {
      if (!this.transactions || !this.transactions.length) {
        this.filteredTransactions = [];
        return;
      }
      
      this.filteredTransactions = this.transactions.filter(item => {
        if (this.filters.location && item.location_id != this.filters.location) return false;
        if (this.filters.salesperson && item.username !== this.filters.salesperson) return false;
        if (this.filters.paymentMethod) {
          if (this.filters.paymentMethod === 'FINANCE') {
            if (!item.payment_methods || !item.payment_methods.some(m => {
              const clean = m.replace(/FINANCE\s*-\s*/i, '').trim();
              return this.financeMethods.some(f => clean.toUpperCase().includes(f)) || 
                     m.toUpperCase().includes('FINANCE');
            })) return false;
          } else if (this.filters.paymentMethod === 'TAX EXEMPT') {
            if (!item.payment_methods || !item.payment_methods.some(m => {
              return this.taxExemptMethods.some(t => m.toUpperCase().includes(t));
            })) return false;
          } else {
            if (!item.payment_methods || !item.payment_methods.some(m => {
              const clean = m.replace(/FINANCE\s*-\s*/i, '').trim();
              return clean === this.filters.paymentMethod || 
                     m === this.filters.paymentMethod ||
                     m.toUpperCase().includes(this.filters.paymentMethod);
            })) return false;
          }
        }
        if (this.filters.saleType === 'refund' && !item.is_refund_entry) return false;
        if (this.filters.saleType === 'layaway' && item.sale_type !== 'layaway') return false;
        if (this.filters.saleType === 'regular' && (item.is_refund_entry || item.sale_type === 'layaway')) return false;
        return true;
      });
      
      this.currentPage = 1;
    },
    
    clearAllFilters() {
      this.filters = { location: null, salesperson: null, paymentMethod: null, saleType: null };
      this.showDetailed = false;
      if (this.activePeriod) {
        this.fetchMainData();
      }
    },
    
    onDateRangeChange() {
      if (this.dateRange && this.dateRange.length === 2) {
        this.rawSummaryData = null;
        this.rawLocationSummary = {};
        this.rawSalespersonSummary = {};
        this.transactions = [];
        this.filteredTransactions = [];
        this.fetchAllKPIs();
        if (this.activePeriod) {
          this.fetchMainData();
        }
      }
    },
    
    renderPieChart() {
      const canvas = this.$refs.pieChart;
      if (!canvas) {
        console.warn('Pie chart canvas not found');
        return;
      }
      
      if (this.pieChart) {
        this.pieChart.destroy();
        this.pieChart = null;
      }
      
      const stats = this.paymentMethodStats;
      if (!stats || !stats.length) {
        this.pieChart = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['No Data'],
            datasets: [{
              data: [1],
              backgroundColor: ['#e2e8f0']
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            cutout: '60%'
          }
        });
        return;
      }
      
      let labels = stats.map(s => s.method);
      let data = stats.map(s => s.amount);
      let backgroundColor = this.colors.slice(0, stats.length);
      
      if (this.filters.paymentMethod === 'FINANCE') {
        const financeItem = stats.find(s => s.method === 'FINANCE');
        if (financeItem && financeItem.subMethods && financeItem.subMethods.length > 0) {
          const subLabels = financeItem.subMethods.map(s => s.name);
          const subData = financeItem.subMethods.map(s => s.amount);
          const subColors = this.colors.slice(0, subData.length);
          
          const otherStats = stats.filter(s => s.method !== 'FINANCE');
          labels = [...otherStats.map(s => s.method), ...subLabels];
          data = [...otherStats.map(s => s.amount), ...subData];
          backgroundColor = [
            ...this.colors.slice(0, otherStats.length),
            ...subColors
          ];
        }
      }
      
      this.pieChart = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: backgroundColor,
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: { size: 11 },
                padding: 10,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                  return `${ctx.label}: $${ctx.parsed.toLocaleString()} (${percentage}%)`;
                }
              }
            }
          },
          cutout: '60%'
        }
      });
    },
    
    loadUserData() {
      try {
        this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const locations = localStorage.getItem('locations');
        if (locations) this.packet.locations = JSON.parse(locations);
        const packet = JSON.parse(localStorage.getItem('packet') || '{}');
        if (packet.locations) this.packet.locations = packet.locations;
      } catch (e) {
        console.error('Error loading user data:', e);
      }
    },
    
    setDefaultDateRange() {
      const today = moment();
      this.dateRange = [
        today.clone().startOf('week').format('MM-DD-YYYY'),
        today.clone().endOf('week').format('MM-DD-YYYY')
      ];
    },
    
    getLocationName(id) {
      if (!this.packet.locations) return `Location ${id}`;
      const loc = this.packet.locations.find(l => l.value == id);
      return loc ? loc.text : `Location ${id}`;
    },
    
    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    
    formatDate(ts) {
      if (!ts) return '-';
      return moment(ts).format('MM-DD-YYYY HH:mm');
    },
    
    exportCSV() {
      if (!this.filteredTransactions.length) {
        this.$bvToast.toast('No data to export', { title: 'Info', variant: 'info' });
        return;
      }
      
      const headers = ['Date', 'Invoice', 'Location', 'Salesperson', 'Payment', 'Amount', 'Type'];
      const rows = this.filteredTransactions.map(item => [
        item.ts,
        item.invoice_id,
        this.getLocationName(item.location_id),
        item.username,
        (item.payment_methods || []).join('; '),
        (item.total_dollar_amount || 0).toFixed(2),
        item.is_refund_entry ? 'Refund' : 'Sale'
      ]);
      
      const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.$bvToast.toast('Export successful!', { title: 'Success', variant: 'success' });
    }
  }
};
</script>

<style scoped>
/* Enhanced Animations & 3D Effects */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

@keyframes pulse-glow {
  0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.2); }
  50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.4); }
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes rotateY {
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
}

/* 3D Loader */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loader-3d {
  width: 60px;
  height: 60px;
  perspective: 600px;
}

.cube {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: rotateCube 2s infinite ease-in-out;
}

@keyframes rotateCube {
  0% { transform: rotateX(-30deg) rotateY(0deg); }
  50% { transform: rotateX(-30deg) rotateY(180deg); }
  100% { transform: rotateX(-30deg) rotateY(360deg); }
}

.cube .face {
  position: absolute;
  width: 60px;
  height: 60px;
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 4px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(139, 92, 246, 0.8));
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.2);
}

.cube .face.front { transform: translateZ(30px); }
.cube .face.back { transform: rotateY(180deg) translateZ(30px); }
.cube .face.right { transform: rotateY(90deg) translateZ(30px); }
.cube .face.left { transform: rotateY(-90deg) translateZ(30px); }
.cube .face.top { transform: rotateX(90deg) translateZ(30px); }
.cube .face.bottom { transform: rotateX(-90deg) translateZ(30px); }

.loading-text {
  margin-top: 20px;
  color: #64748b;
  font-size: 14px;
  animation: pulse-text 1.5s ease-in-out infinite;
}

@keyframes pulse-text {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Period Cards with 3D Hover */
.period-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.period-card {
  perspective: 1000px;
  background: transparent;
  border: none;
  text-align: center;
}

.period-card-inner {
  background: white;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
  border: 2px solid transparent;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  transform-style: preserve-3d;
  transform: rotateX(0) rotateY(0);
}

.period-card-inner:hover {
  transform: translateY(-8px) rotateX(2deg) rotateY(2deg);
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
}

.period-card.active .period-card-inner {
  border-color: #6366f1;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  box-shadow: 0 8px 30px rgba(99, 102, 241, 0.25);
  transform: translateY(-4px) rotateX(1deg);
}

.period-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.period-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 600;
}

.period-change {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  transition: all 0.3s;
}

.period-change.positive {
  color: #10b981;
  background: #d1fae5;
}

.period-change.negative {
  color: #ef4444;
  background: #fecaca;
}

.period-amount {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 2px 0;
  transition: all 0.3s;
}

.period-card:hover .period-amount {
  transform: scale(1.05);
}

.period-transactions {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.period-card-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
  border-radius: 0 0 12px 12px;
  transition: all 0.3s ease;
}

.period-card-indicator.active {
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.period-donut-container {
  flex: 1;
  min-height: 120px;
  width: 100%;
  margin-top: 4px;
}

.period-donut {
  width: 100% !important;
  height: 100% !important;
}

/* Modal Styles */
.period-modal >>> .modal-content {
  border-radius: 20px;
  overflow: hidden;
  background: #f8fafc;
}

.period-modal >>> .modal-header {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border-bottom: none;
  padding: 20px 28px;
  position: relative;
  overflow: hidden;
}

.period-modal >>> .modal-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.1), transparent 70%);
  animation: pulse-glow 4s ease-in-out infinite;
}

.period-modal >>> .modal-header .close {
  color: white;
  opacity: 0.8;
  transition: all 0.3s;
}

.period-modal >>> .modal-header .close:hover {
  opacity: 1;
  transform: rotate(90deg);
}

.period-modal >>> .modal-body {
  padding: 24px 28px;
}

/* Modal Summary with Icons */
.modal-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.modal-summary-item {
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s;
  animation: slideInUp 0.6s ease forwards;
}

.modal-summary-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
}

.modal-summary-item:nth-child(1) { animation-delay: 0.1s; }
.modal-summary-item:nth-child(2) { animation-delay: 0.2s; }
.modal-summary-item:nth-child(3) { animation-delay: 0.3s; }
.modal-summary-item:nth-child(4) { animation-delay: 0.4s; }

.summary-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.modal-summary-item .label {
  display: block;
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-summary-item .value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-top: 2px;
}

/* Carousel */
.carousel-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
}

.carousel-btn {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #1e293b;
}

.carousel-btn:hover:not(:disabled) {
  background: #6366f1;
  color: white;
  border-color: #6366f1;
  transform: scale(1.1) rotate(-10deg);
}

.carousel-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel-dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #cbd5e1;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.dot:hover {
  background: #94a3b8;
  transform: scale(1.2);
}

.dot.active {
  background: #6366f1;
  width: 28px;
  border-radius: 10px;
}

/* Carousel Container - No mouse scroll */
.carousel-container {
  overflow: hidden;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.carousel-track {
  display: flex;
  transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-slide {
  flex: 0 0 100%;
  padding: 20px;
  min-height: 300px;
  animation: scaleIn 0.5s ease;
}

.slide-content {
  min-height: 300px;
}

.slide-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.slide-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.slide-title h6 {
  margin: 0;
  font-weight: 600;
  color: #1e293b;
}

.slide-icon {
  font-size: 22px;
  animation: float 3s ease-in-out infinite;
}

.slide-count {
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 12px;
}

.slide-counter {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 12px;
}

/* Tables with hover effects */
.modal-table {
  width: 100%;
  font-size: 13px;
  border-collapse: collapse;
}

.modal-table th,
.modal-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.3s;
}

.modal-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #475569;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-table tbody tr {
  transition: all 0.3s;
}

.modal-table tbody tr:hover {
  background: #f8fafc;
  transform: scale(1.01);
}

.modal-table tfoot tr {
  background: #f1f5f9;
  font-weight: 600;
}

.modal-table .percentage-badge {
  background: #eef2ff;
  color: #4338ca;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s;
}

.modal-table .percentage-badge:hover {
  transform: scale(1.1);
}

.clickable-rows tbody tr {
  cursor: pointer;
  transition: all 0.3s;
}

.clickable-rows tbody tr:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.clickable-rows tbody tr:active {
  background: #dbeafe;
}

.table-hint {
  font-size: 12px;
  color: #64748b;
  margin-top: 10px;
  padding: 10px 16px;
  background: #f8fafc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  animation: slideInUp 0.5s ease;
}

/* Payment Methods in Drill Down */
.payment-breakdown {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 24px;
}

.payment-chart-container {
  height: 280px;
  width: 100%;
  animation: scaleIn 0.6s ease;
}

.payment-list-modal {
  max-height: 320px;
  overflow-y: auto;
}

.payment-item-modal {
  padding: 8px 10px;
  border-radius: 8px;
  transition: all 0.3s;
}

.payment-item-modal:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.payment-row-modal {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.payment-name-modal {
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.payment-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  transition: all 0.3s;
}

.payment-dot:hover {
  transform: scale(1.5);
}

.payment-amount-modal {
  font-weight: 600;
  font-size: 13px;
}

.payment-bar-modal {
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  margin: 4px 0;
  overflow: hidden;
}

.payment-bar-fill-modal {
  height: 100%;
  border-radius: 4px;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 0 0;
  border-top: 1px solid #e2e8f0;
  margin-top: 12px;
}

.table-wrap {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}

/* Drill Down Modal */
.drilldown-modal >>> .modal-content {
  border-radius: 20px;
  overflow: hidden;
  background: #f8fafc;
}

.drilldown-modal >>> .modal-header {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border-bottom: none;
  padding: 20px 28px;
}

.drilldown-modal >>> .modal-header .close {
  color: white;
  opacity: 0.8;
}

.drilldown-modal >>> .modal-header .close:hover {
  opacity: 1;
  transform: rotate(90deg);
}

.drilldown-modal >>> .modal-body {
  padding: 24px 28px;
}

.drilldown-content {
  margin-top: 16px;
}

.drilldown-section {
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s;
  animation: slideInUp 0.5s ease;
}

.drilldown-section:hover {
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
}

.drilldown-section-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

.drilldown-payment-grid {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
}

.drilldown-chart-container {
  height: 220px;
  width: 100%;
  animation: scaleIn 0.6s ease;
}

.drilldown-payment-list {
  max-height: 220px;
  overflow-y: auto;
}

/* Active Banner */
.active-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  background: linear-gradient(135deg, #eef2ff, #dbeafe);
  border-radius: 16px;
  margin-bottom: 16px;
  border: 2px solid #6366f1;
  animation: slideInUp 0.5s ease;
}

.banner-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.banner-label {
  font-size: 13px;
  font-weight: 600;
  color: #4338ca;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.banner-amount {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.banner-count {
  font-size: 12px;
  color: #64748b;
}

.banner-close {
  color: #64748b;
  padding: 0;
  font-size: 18px;
  transition: all 0.3s;
}

.banner-close:hover {
  color: #1e293b;
  transform: rotate(90deg);
}

/* Filters */
.filters-section {
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  animation: slideInUp 0.5s ease;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.filter-select {
  border-radius: 8px;
  font-size: 12px;
  background: #f8fafc;
  border-color: #e2e8f0;
  transition: all 0.3s;
}

.filter-select:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.date-range-wrapper {
  display: flex;
  gap: 8px;
}

.custom-datepicker {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  background: #f8fafc;
  transition: all 0.3s;
}

.custom-datepicker:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.analyze-btn {
  border-radius: 8px;
  padding: 4px 16px;
  transition: all 0.3s;
}

.analyze-btn:hover {
  transform: scale(1.05);
}

.filter-clear {
  margin-top: 10px;
}

.filter-clear .btn-link {
  font-size: 12px;
  color: #6366f1;
  transition: all 0.3s;
}

.filter-clear .btn-link:hover {
  color: #4f46e5;
  text-decoration: none;
  transform: translateX(4px);
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideInUp 0.6s ease forwards;
}

.summary-card:nth-child(1) { animation-delay: 0.1s; }
.summary-card:nth-child(2) { animation-delay: 0.2s; }
.summary-card:nth-child(3) { animation-delay: 0.3s; }
.summary-card:nth-child(4) { animation-delay: 0.4s; }

.summary-card:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 12px 40px rgba(0,0,0,0.08);
}

.summary-icon {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: all 0.4s;
}

.summary-card:hover .summary-icon {
  transform: rotateY(180deg) scale(1.1);
}

.summary-icon.total-sales { 
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  animation: float 3s ease-in-out infinite;
}
.summary-icon.net-sales { 
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  animation: float 3s ease-in-out infinite 0.5s;
}
.summary-icon.orders { 
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  animation: float 3s ease-in-out infinite 1s;
}
.summary-icon.avg-order { 
  background: linear-gradient(135deg, #ede9fe, #c4b5fd);
  animation: float 3s ease-in-out infinite 1.5s;
}

.summary-info {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.summary-value {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  transition: all 0.3s;
}

.summary-card:hover .summary-value {
  transform: scale(1.05);
}

/* Payment Section */
.payment-section {
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  animation: slideInUp 0.5s ease;
}

.section-header {
  font-weight: 600;
  font-size: 15px;
  padding-bottom: 14px;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.payment-grid {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 24px;
}

.pie-chart-wrapper {
  height: 280px;
  width: 100%;
  animation: scaleIn 0.6s ease;
}

.payment-list {
  max-height: 320px;
  overflow-y: auto;
}

.payment-item {
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s;
}

.payment-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.payment-item.drill-active {
  background: #dbeafe;
  border-left: 3px solid #6366f1;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.payment-name {
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.payment-amount {
  font-weight: 600;
  font-size: 13px;
}

.payment-bar {
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  margin: 4px 0;
  overflow: hidden;
}

.payment-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.sub-methods {
  padding: 4px 8px 4px 28px;
  border-left: 2px solid #e2e8f0;
  margin-left: 16px;
}

.sub-method-item {
  display: flex;
  justify-content: space-between;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.sub-method-item:hover {
  background: #f1f5f9;
}

.sub-method-item.drill-active {
  background: #dbeafe;
}

.sub-method-name { color: #475569; }
.sub-method-amount { font-weight: 600; color: #1e293b; }

/* Performance Grid */
.performance-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.performance-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  animation: slideInUp 0.5s ease;
}

.performance-card:hover {
  box-shadow: 0 8px 30px rgba(0,0,0,0.08);
}

.performance-header {
  padding: 12px 20px;
  background: #f8fafc;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
}

.performance-table-wrap {
  overflow-x: auto;
  max-height: 280px;
  overflow-y: auto;
}

.performance-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.performance-table th,
.performance-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.3s;
}

.performance-table th {
  background: #fafbfc;
  font-weight: 600;
  color: #475569;
  position: sticky;
  top: 0;
  z-index: 1;
}

.performance-table tbody tr {
  cursor: pointer;
  transition: all 0.3s;
}

.performance-table tbody tr:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.performance-table tbody tr.drill-active {
  background: #dbeafe !important;
  border-left: 3px solid #6366f1;
}

.performance-table tfoot tr {
  background: #f8fafc;
  font-weight: 600;
}

/* No Filters Message */
.no-filters-message {
  text-align: center;
  padding: 50px;
  background: #f8fafc;
  border-radius: 16px;
  margin-bottom: 16px;
  animation: slideInUp 0.5s ease;
}

.no-filters-message .icon {
  font-size: 48px;
  color: #94a3b8;
  margin-bottom: 12px;
  animation: float 3s ease-in-out infinite;
}

.no-filters-message p {
  color: #64748b;
  margin: 0;
}

/* Detailed Section */
.detailed-section {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  margin-top: 16px;
  animation: slideInUp 0.5s ease;
}

.detailed-header {
  padding: 12px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
}

.table-pagination {
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  animation: scaleIn 0.5s ease;
}

.empty-icon-wrapper {
  display: inline-block;
  animation: float 3s ease-in-out infinite;
}

.empty-icon {
  font-size: 56px;
  color: #94a3b8;
}

/* Slide Transitions */
.slide-fade-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-fade-enter {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Scrollbar Styling */
.table-wrap::-webkit-scrollbar,
.payment-list-modal::-webkit-scrollbar,
.payment-list::-webkit-scrollbar,
.drilldown-payment-list::-webkit-scrollbar,
.performance-table-wrap::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-wrap::-webkit-scrollbar-track,
.payment-list-modal::-webkit-scrollbar-track,
.payment-list::-webkit-scrollbar-track,
.drilldown-payment-list::-webkit-scrollbar-track,
.performance-table-wrap::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.table-wrap::-webkit-scrollbar-thumb,
.payment-list-modal::-webkit-scrollbar-thumb,
.payment-list::-webkit-scrollbar-thumb,
.drilldown-payment-list::-webkit-scrollbar-thumb,
.performance-table-wrap::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 3px;
  transition: all 0.3s;
}

.table-wrap::-webkit-scrollbar-thumb:hover,
.payment-list-modal::-webkit-scrollbar-thumb:hover,
.payment-list::-webkit-scrollbar-thumb:hover,
.drilldown-payment-list::-webkit-scrollbar-thumb:hover,
.performance-table-wrap::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
}

/* Responsive */
@media (max-width: 992px) {
  .period-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .payment-grid {
    grid-template-columns: 1fr;
  }
  .pie-chart-wrapper {
    height: 240px;
    max-width: 350px;
    margin: 0 auto;
  }
  .performance-grid {
    grid-template-columns: 1fr;
  }
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .modal-summary {
    grid-template-columns: repeat(2, 1fr);
  }
  .payment-breakdown {
    grid-template-columns: 1fr;
  }
  .payment-chart-container {
    height: 220px;
    max-width: 300px;
    margin: 0 auto;
  }
  .drilldown-payment-grid {
    grid-template-columns: 1fr;
  }
  .drilldown-chart-container {
    height: 200px;
    max-width: 300px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .period-grid {
    grid-template-columns: 1fr 1fr;
  }
  .banner-info {
    flex-wrap: wrap;
    gap: 8px;
  }
  .banner-amount {
    font-size: 18px;
  }
  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 576px) {
  .period-grid {
    grid-template-columns: 1fr;
  }
  .period-card {
    padding: 12px;
    min-height: 180px;
  }
  .period-amount {
    font-size: 20px;
  }
  .period-donut-container {
    min-height: 100px;
  }
  .summary-grid {
    grid-template-columns: 1fr;
  }
  .summary-value {
    font-size: 18px;
  }
  .pie-chart-wrapper {
    height: 200px;
    max-width: 250px;
  }
  .modal-summary {
    grid-template-columns: 1fr 1fr;
  }
  .modal-summary-item .value {
    font-size: 16px;
  }
  .payment-breakdown {
    grid-template-columns: 1fr;
  }
  .payment-chart-container {
    height: 200px;
    max-width: 250px;
  }
  .carousel-btn {
    width: 32px;
    height: 32px;
  }
  .drilldown-chart-container {
    height: 180px;
    max-width: 250px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/SalesReport.vue